<section class="hero wave-bg">
  <div class="container hero-content">
    <h1>
      Tierce Maintenance Applicative (TMA)
      <span class="accent"> Spécialisée Node.js & NestJS</span>
    </h1>
    <p class="hero-description">
      Votre backend Node.js est le moteur de votre activité. Ne laissez pas la dette technique, les fuites de mémoire ou les vulnérabilités de sécurité ralentir votre croissance. Nos experts seniors assurent une maintenance proactive et évolutive.
    </p>
    <div class="cta-row">
      <a 
        href="https://cal.mesh-box.fr/ogirodin/30min"
        class="button primary large" 
        data-cal-link="ogirodin/30min" 
        data-cal-config='{"layout":"month_view"}'
      >
        Auditer mon backend Node.js
      </a>
    </div>
  </div>
</section>

<section class="section alt">
  <div class="container max-w-4xl">
    <h2>Pourquoi une expertise TMA dédiée à l'écosystème Node.js ?</h2>
    <p class="mb-6">
      Node.js est devenu le standard pour construire des applications scalables et performantes. Cependant, la rapidité d'évolution de son écosystème (NPM) et la complexité de la gestion de l'asynchronisme exigent une attention constante. Une mauvaise gestion de la boucle d'événements (Event Loop) ou des dépendances non suivies peuvent rapidement transformer une application performante en un gouffre technique.
    </p>
    <h3>Maîtrise de l'asynchronisme et des performances backend</h3>
    <p class="mb-6">
      Chez Mesh Box, nous intervenons sur les briques les plus sensibles de votre architecture. Nous optimisons vos entrées/sorties (I/O), gérons les caches distribués avec Redis et assurons que vos microservices communiquent de manière fluide et sécurisée. Notre objectif est de garantir un temps de réponse minimal pour vos APIs, quel que soit le volume de trafic.
    </p>
  </div>
</section>

<section class="section">
  <div class="container">
    <h2>Nos piliers de maintenance pour vos APIs et Microservices</h2>
    <div class="grid-2">
      <div class="card">
        <h3>Optimisation & Scalabilité</h3>
        <p>Analyse des performances via APM (Application Performance Monitoring). Nous identifions les requêtes lentes, optimisons les accès base de données (PostgreSQL, MongoDB) et mettons en place des stratégies de clustering pour une haute disponibilité.</p>
      </div>
      <div class="card">
        <h3>Sécurité & Veille NPM</h3>
        <p>Le registre NPM est une cible privilégiée. Nous effectuons une veille constante sur les CVE (Common Vulnerabilities and Exposures) et appliquons les patchs de sécurité de manière proactive, tout en assurant la non-régression de vos fonctionnalités.</p>
      </div>
      <div class="card">
        <h3>Migration & Modernisation</h3>
        <p>Votre code est en JavaScript pur ? Nous vous accompagnons dans la migration vers TypeScript pour bénéficier d'un typage fort et d'une meilleure maintenabilité. Nous maîtrisons également les montées de version majeures de Node.js et NestJS.</p>
      </div>
      <div class="card">
        <h3>Observabilité & Monitoring</h3>
        <p>Nous intégrons des solutions d'observabilité complètes (Grafana, Sentry, Datadog) pour détecter les anomalies en temps réel. Un incident est identifié avant même qu'il ne soit ressenti par l'utilisateur final.</p>
      </div>
    </div>
  </div>
</section>

<section class="section alt">
  <div class="container max-w-4xl">
    <h2>Une méthodologie rigoureuse pour votre sérénité</h2>
    <p class="mb-8">
      L'externalisation de votre TMA Node.js ne doit pas sacrifier l'agilité. Nous travaillons en intégration étroite avec vos équipes, en utilisant des outils de pilotage modernes et transparents.
    </p>
     <div class="grid-2 gap-8 mb-10">
      <div class="flex items-start gap-3">
        <span class="text-brand font-bold text-xl">✓</span>
        <div>
          <h4 class="font-bold">Gestion des tickets agile</h4>
          <p class="text-sm">Suivi précis via Jira ou Linear avec des priorités clairement définies.</p>
        </div>
      </div>
      <div class="flex items-start gap-3">
        <span class="text-brand font-bold text-xl">✓</span>
        <div>
          <h4 class="font-bold">Engagements SLA stricts</h4>
          <p class="text-sm">Respect des délais de prise en compte (GTI) et de rétablissement (GTR) contractuels.</p>
        </div>
      </div>
      <div class="flex items-start gap-3">
        <span class="text-brand font-bold text-xl">✓</span>
        <div>
          <h4 class="font-bold">Documentation technique</h4>
          <p class="text-sm">Mise à jour constante du Swagger/OpenAPI et du README technique.</p>
        </div>
      </div>
      <div class="flex items-start gap-3">
        <span class="text-brand font-bold text-xl">✓</span>
        <div>
          <h4 class="font-bold">Audit de code continu</h4>
          <p class="text-sm">Revue de code systématique pour maintenir un haut niveau de qualité.</p>
        </div>
      </div>
    </div>
    
    <div class="bg-card p-6 rounded-lg border-l-4 border-brand shadow-sm">
      <h3 class="flex items-center gap-2">🏛️ Éligibilité Crédit Impôt Innovation (CII)</h3>
      <p>Grâce à notre agrément CII n°29987181, une partie de vos dépenses de maintenance évolutive liée à l'innovation peut être récupérée sous forme de crédit d'impôt. C'est un levier majeur pour pérenniser votre R&D.</p>
    </div>
  </div>
</section>

<section class="section">
  <div class="container text-center">
    <h2>Au-delà du Backend</h2>
    <p class="mb-8">Une application performante, c'est aussi un frontend irréprochable. Découvrez nos autres expertises.</p>
    <div class="flex justify-center gap-6 flex-wrap">
      <a routerLink="/tma-angular" class="button outline">TMA Angular</a>
      <a routerLink="/tma-android" class="button outline">TMA Android</a>
      <a routerLink="/refonte-modernisation-legacy" class="button outline">Modernisation Legacy</a>
    </div>
    <p class="mt-10 text-sm text-muted">Pour en savoir plus sur les bonnes pratiques du runtime, consultez la <a href="https://nodejs.org/en/docs/" target="_blank" rel="noopener noreferrer" class="underline">documentation officielle de Node.js</a>.</p>
  </div>
</section>

<section class="section cta-final alt">
  <div class="container">
    <h2>Prêt à sécuriser votre moteur backend ?</h2>
    <p>Ne laissez pas les bugs s'accumuler. Parlons de votre stratégie de maintenance dès aujourd'hui.</p>
    <a 
      href="https://cal.mesh-box.fr/ogirodin/30min"
      class="button primary large" 
      data-cal-link="ogirodin/30min" 
    >
      Demander un audit TMA
    </a>
    <br/>
    <a routerLink="/maintenance-tma-nodejs-angular" style="display: inline-block; margin-top: 1.5rem; color: var(--brand); font-weight: bold;">
      ← Voir toutes nos offres de maintenance
    </a>
  </div>
</section>